import { wccModules } from 'enums/wccModules';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { DisposableSubscribable, Observable, ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import ActiveURLHashManager from 'managers/activeURLHash';
import AdminAccessManager from 'managers/adminAccess';
import AdminCommunityPagesManager from 'managers/community/pages/admin';
import CommunityPagesManager from 'managers/community/pages/base';
import PublicCommunityPagesManager from 'managers/community/pages/public';
import { IWCCStorageManager } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import CommunityPage from 'models/community/page';
import WCCEntity from 'models/entity';
import { SimpleTopic } from 'models/simpleTopic';
import { SimpleTopicsGroup } from 'models/simpleTopicsGroup';
import { LoginService } from 'services/login';
import CommunityTopicsManager from '../../../../managers/community/communityTopicsManager';
import CommunityNavigationWidgetLinkType from './enums/types';
import CommunityNavigationWidgetItemModel, { CommunityNavigationWidgetItemConfig } from './item';
import './link.html';
import './link.scss';

const router = require('models/router');
const activeHash = ActiveURLHashManager.instance.hash;

const groupPattern = /{group:([0-9a-f]+(?:-[0-9a-f]+)*)}/i;
const topicPattern = /{topic:([0-9a-f]+(?:-[0-9a-f]+)*)}/i;
const richContentPattern = /<span[^>]+><\/span>/;
const cssPattern = /^[\w\d- ]+$/;

export interface CommunityNavigationWidgetLinkConfig extends CommunityNavigationWidgetItemConfig {
    CSS?: SubscribableOrNullableValue<string>
    Type?: SubscribableOrNullableValue<CommunityNavigationWidgetLinkType>
    Route?: SubscribableOrNullableValue<string>
    TopicId?: SubscribableOrNullableValue<string>
    IsProtected?: SubscribableOrNullableValue<boolean>
    AccessRestrictions?: SubscribableOrNullableValue<Array<number>>

    css?: SubscribableOrNullableValue<string>
    type?: SubscribableOrNullableValue<CommunityNavigationWidgetLinkType>
    route?: SubscribableOrNullableValue<string>
    topicId?: SubscribableOrNullableValue<string>
    isProtected?: SubscribableOrNullableValue<boolean>
    isHighlighted?: SubscribableOrNullableValue<boolean>
    accessRestrictions?: SubscribableOrNullableValue<Array<number>>

    toDoCount?: SubscribableOrNullableValue<number>
}

export class CommunityNavigationWidgetLinkModel extends CommunityNavigationWidgetItemModel<CommunityNavigationWidgetLinkConfig> {
    css: Observable<string>
    type: Observable<CommunityNavigationWidgetLinkType>
    route: Observable<string | undefined>
    topicId: Observable<string | undefined>
    toDoCount: Observable<number>
    isProtected: Observable<boolean>
    isHighlighted: Observable<boolean>
    accessRestrictions: ObservableArray<number>    

    constructor(data?: CommunityNavigationWidgetLinkConfig) {
        super(data);

        this.css = this.createField(data, 'css', '');
        this.type = this.createField(data, 'type', CommunityNavigationWidgetLinkType.url);
        this.route = this.createField(data, 'route');
        this.topicId = this.createField(data, 'topicId');
        this.toDoCount = this.createField(data, 'toDoCount', 0);
        this.isProtected = this.createField(data, 'isProtected', false);
        this.isHighlighted = this.createField(data, 'isHighlighted', false);
        this.accessRestrictions = this.createCollection(data, 'accessRestrictions');
    }

    override toJson(forExport = true) {
        let json = super.toJson(forExport);

        if (forExport)
            json = _(json).omit('toDoCount');

        return json;
    }

    override clone<V extends WCCEntity<CommunityNavigationWidgetLinkConfig> = WCCEntity<CommunityNavigationWidgetLinkConfig>>(overrides?: Partial<CommunityNavigationWidgetLinkConfig>): V {
        return super.clone<V>({
            ...overrides,

            toDoCount: this.toDoCount
        });
    }

    validate() {
        return [
            this.validateCSS(),
            this.validateTitle(),
            this.validateRoute()
        ].every(error => error == undefined);
    }

    validateCSS() {
        var css = this.css();

        if (css.length > 0) {
            if (!cssPattern.test(css))
                return 'Invalid CSS. Only [a-zA-Z0-9_] allowed';
        }
    }

    validateTitle() {
        return this.title().length == 0 ? messages.FieldIsMandatory : undefined;
    }

    validateRoute() {
        switch (this.type()) {
            case CommunityNavigationWidgetLinkType.url:
                return !_(this.route()).any() ? messages.FieldIsMandatory : undefined;
        }
    }

    protected override getId() {
        return 'link:' + this.title();
    }

    protected override normalizeData(data: CommunityNavigationWidgetLinkConfig) {
        super.normalizeData(data);

        data.css ??= data.CSS;
        data.type ??= data.Type;
        data.route ??= data.Route;
        data.topicId ??= data.TopicId;
        data.isProtected ??= data.IsProtected;
        data.accessRestrictions ??= data.AccessRestrictions;
    }
}

@injectable()
export default class CommunityNavigationWidgetLinkVM {
    private adminAccess: AdminAccessManager
    private defaultPages: Subscribable<Array<CommunityPage>>

    private type: Subscribable<CommunityNavigationWidgetLinkType>
    private route: Subscribable<string | undefined>

    private groups: Subscribable<Array<SimpleTopicsGroup>>
    private topics: Subscribable<Array<SimpleTopic>>

    private isDemo: Subscribable<boolean>
    private isDefault: Subscribable<boolean>

    static Model = CommunityNavigationWidgetLinkModel;

    busy = ko.observable(false);

    labels = labels;

    title: Subscribable<string>
    toDoCount: Subscribable<number>

    showToDo: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) model: CommunityNavigationWidgetLinkModel,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.loginService) private loginService: LoginService,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const { discussionId, isAdmin, isLoggedIn } = settings;

        this.adminAccess = effects.register(new AdminAccessManager());

        const pagesManager = isAdmin ? <DisposableSubscribable<CommunityPagesManager | undefined>>storage.get(AdminCommunityPagesManager, {}) : <DisposableSubscribable<CommunityPagesManager | undefined>>storage.get(PublicCommunityPagesManager, {});
        const pages = pagesManager.pluck(m => m.pages, []);

        this.defaultPages = pages
            .filter(page => page.startUpOptions() !== enums.CommunityPageStartUpOptions.IfLoggedIn.value || isLoggedIn())
            .filter(page => page.startUpOptions() !== enums.CommunityPageStartUpOptions.IfNotLoggedIn.value || !isLoggedIn())
            .filter(page => page.startUpOptions() !== enums.CommunityPageStartUpOptions.Never.value);

        const topicsManager = storage.get(CommunityTopicsManager, { discussionId });
        this.groups = topicsManager.pluck(m => m.groups, []);
        this.topics = topicsManager.pluck(m => m.topics, []);

        const css = model.css;
        this.title = model.title.pluck(title => this.processTitle(title));
        this.type = model.type;
        this.route = model.route;
        const isRoot = model.isRoot;
        this.isDemo = model.demo;

        this.isDefault = ko.pureComputed(() => this.checkIfDefault());
        const isActive = ko.pureComputed(() => this.checkIfActive());
        const isValid = ko.pureComputed(() => this.checkIfValid());

        this.toDoCount = model.toDoCount;
        this.showToDo = this.toDoCount.is(count => count > 0);

        ko.applyBindingsToNode(info.element, {
            'click': () => this.navigate(),
            'visible': isValid,

            'css': {
                'community__navigation__link': true,
                'community__navigation__link--active': isActive,
                'community__navigation__link--highlighted': model.isHighlighted,
                'community__navigation__link--root': isRoot,
                'community__navigation__link--logout': this.type.is(CommunityNavigationWidgetLinkType.logout),
                'active': isActive,
                'community-navigation-logout': this.type.is(CommunityNavigationWidgetLinkType.logout)
            },

            'attr': {
                'data-todo': this.toDoCount
            }
        });

        ko.applyBindingsToNode(info.element, {
            'css': css
        });
    }

    navigate() {
        if (this.isDemo()) return;

        switch (this.type()) {
            case CommunityNavigationWidgetLinkType.logout:
                this.logout();
                break;

            case CommunityNavigationWidgetLinkType.moderatorTools:
            case CommunityNavigationWidgetLinkType.observerTools:
                this.showAdminTools();
                break;

            case CommunityNavigationWidgetLinkType.todo:
                this.openNextTopicToDo();
                break;

            case CommunityNavigationWidgetLinkType.communityProject:
                window.location.href = `/${this.route()}` ?? '';
                break;

            default:
                if (this.route() != null)
                    router.navigate(this.route());

                break;
        }
    }

    private checkIfValid() {
        if (this.isDemo()) return true;

        switch (this.type()) {
            case CommunityNavigationWidgetLinkType.moderatorTools:
            case CommunityNavigationWidgetLinkType.observerTools:
                return this.adminAccess.hasAccess();

            default: return true;
        }
    }

    private logout() {
        this.loginService.dropToken();
        router.navigate('brochure');
    }

    private openNextTopicToDo() {
        const todo = this.topics().find(topic => topic.shouldBeAnswered()) ?? this.topics()[0];

        if (todo != undefined)
            router.navigate('topic/' + settings.discussionId + '/' + todo.id());
    }

    private async showAdminTools() {
        this.busy(true);

        await this.adminAccess.requestAccess();

        this.busy(false);
    }

    private checkIfDefault() {
        return this.route() != undefined && this.defaultPages().some(page => page.url() == this.route());
    }

    private checkIfActive() {
        return this.type() == CommunityNavigationWidgetLinkType.url &&
            (activeHash() == this.route() || activeHash() == '' && this.isDefault());
    }

    private processTitle(title: string) {
        return new Array<Func<string, [string]>>(
            title => this.processGroupsInTitle(title),
            title => this.processTopicsInTitle(title),
            title => this.processContentInTitle(title)
        ).reduce((result, func) => func(result), title);
    }

    private processGroupsInTitle(title: string) {
        let step = 0,
            success = false,
            match: RegExpExecArray | null;

        do {
            match = groupPattern.exec(title);
            success = false;

            if (match != undefined) {
                const [item, groupId] = match;

                let lIdx = match.index,
                    rIdx = match.index + item.length;

                let group = this.groups().find(group => group.id() == groupId);

                if (group != undefined) {
                    title = title.substring(0, lIdx) + group.title() + title.substring(rIdx + 1);

                    success = true;
                    step++;
                }
            }
        }
        while (success && step < 200);

        return title;
    }

    private processTopicsInTitle(title: string) {
        let step = 0,
            success = false,
            match: RegExpExecArray | null;

        do {
            match = topicPattern.exec(title);
            success = false;

            if (match != undefined) {
                const [item, topicId] = match;

                let lIdx = match.index,
                    rIdx = match.index + item.length;

                let topic = this.topics().find(topic => topic.id() == topicId);

                if (topic !== undefined) {
                    title = title.substring(0, lIdx) + topic.title() + title.substring(rIdx + 1);

                    success = true;
                    step++;
                }
            }
        }
        while (success && step < 200);

        return title;
    }

    private processContentInTitle(title: string) {
        if (title.length > 40) {
            var hasRichContent = richContentPattern.test(title);

            if (!hasRichContent)
                return title.substr(0, 40) + '...';
        }

        return title;
    }
}